import { GET_CLUSTER_TEAM, GET_RANKING_BY_TEAM, GET_TEAM_ACTIVE_SECRET_SANTA, GET_TEAM_ACTIVE_SOCIAL } from 'constants/api'
import { createContext, Dispatch, FunctionComponent, useContext, useEffect, useReducer, useState } from 'react'
import LoadingScreen from 'components/loading-screen'
import { useSession } from 'contexts/session-context'
import axios from 'axios'
import groupsReducer, { ActiveDynamic, Cluster, Group, GroupsAction, GroupsState, RankingGroup } from './groups-reducer'
import CreateGroup from './CreateGroup'
import HowDoYouWantToParticipate from './HowDoYouWantToParticipate'

export interface UserContextProps {
  state: GroupsState
  dispatch: Dispatch<GroupsAction>
}

const defaultState: GroupsState = {
  groups: [],
  openCreate: false,
  participateGroupId: null,
  openSelectParticipate: false
}

const GroupsContext = createContext<UserContextProps>({} as UserContextProps)

export const GroupsProvider: FunctionComponent = ({ children }) => {
  const [state, dispatch] = useReducer(groupsReducer, defaultState)
  const [{ data: session }] = useSession()

  const [load, setLoad] = useState(false)

  const setOpenCreate = (value: boolean) => dispatch({ type: 'SET_OPEN_CREATE', payload: value })

  const getGroupsSession = async () => {
    setLoad(true)
    try {
      const headers = { Authorization: `Bearer ${(session as any)._tokenResponse?.idToken}`, 'Ph-Api-Id': PH_API_ID ?? 1 }
      const { data: dynamicSocial } = await axios.get<ActiveDynamic>(GET_TEAM_ACTIVE_SOCIAL, { headers })
      const { data: dynamicSecretSanta } = await axios.get<ActiveDynamic>(GET_TEAM_ACTIVE_SECRET_SANTA, { headers })

      if (dynamicSocial) {
        dispatch({ type: 'SET_DYNAMIC', payload: dynamicSocial })
        const { data: groups } = await axios.get(GET_CLUSTER_TEAM.replace(':teamId', `${dynamicSocial.id}`), { headers })

        if (groups) {
          dispatch({ type: 'SET_GROUPS', payload: groups })
        }
      }

      if (dynamicSecretSanta) {
        dispatch({ type: 'SET_DYNAMIC', payload: dynamicSecretSanta })
        const { data: groups } = await axios.get(GET_CLUSTER_TEAM.replace(':teamId', `${dynamicSecretSanta.id}`), { headers })

        if (groups) {
          dispatch({ type: 'SET_GROUPS', payload: groups })
        }
      }

    } catch (error) {
      console.error(error)
    } finally {
      setLoad(false)
    }
  }

  useEffect(() => {
    if (session && !load && !state?.dynamic) {
      getGroupsSession()
    }
  }, [session])

  return (
    <GroupsContext.Provider value={{ state, dispatch }}>
      {load && <LoadingScreen />}
      {state.openCreate && <CreateGroup setOpenCreate={setOpenCreate} />}
      {state.openSelectParticipate && <HowDoYouWantToParticipate />}
      {children}
    </GroupsContext.Provider>
  )
}

export const useGroups = () => {
  const { state, dispatch } = useContext(GroupsContext)
  const [{ data: session }] = useSession()

  const setOpenSelectParticipate = (value: boolean) => dispatch({ type: 'SET_OPEN_SELECT_PARTICIPATE', payload: value })
  const setParticipateGroupId = (value: number | null) => dispatch({ type: 'SET_PARTICIPATE_GROUP_ID', payload: value })
  const setCallbackParticipate = (value: (clusterId: number) => void) => dispatch({ type: 'SET_CALLBACK_PARTICIPATE', payload: value })

  const handleOpenSelectParticipate = (teamId: number, callback?: () => void) => {

    if (state?.dynamic && teamId === state?.dynamic?.id) {
      if (callback) setCallbackParticipate(callback)
      return setOpenSelectParticipate(true)
    }

    setCallbackParticipate(() => null)
    setParticipateGroupId(null)
    setOpenSelectParticipate(false)

    if (callback) {
      return callback()
    }
  }

  const getRanking = async (clusterId: number) => {
    const headers = { Authorization: `Bearer ${(session as any)._tokenResponse?.idToken}`, 'Ph-Api-Id': PH_API_ID ?? 1 }
    const { data: { profileRanking, ranking } } = await axios.get<{ profileRanking: RankingGroup, ranking: RankingGroup[] }>(GET_RANKING_BY_TEAM.replace(':teamId', `${state?.dynamic?.id}`).replace(':clusterId', `${clusterId}`), { headers })
    return { profileRanking, ranking }
  }

  const setNewGroups = (group: { cluster: Cluster, group: Group[] }) => dispatch({ type: 'SET_NEW_GROUPS', payload: group })
  const setUpdateGroup = (group: { cluster: Cluster, group: Group[] }) => dispatch({ type: 'SET_UPDATE_GROUP', payload: group })
  const setRemoveGroup = (groupId: number) => dispatch({ type: 'SET_REMOVE_GROUP', payload: groupId })
  const setDynamic = (dynamic: ActiveDynamic) => dispatch({ type: 'SET_DYNAMIC', payload: dynamic })
  const setOpenCreate = (value: boolean) => dispatch({ type: 'SET_OPEN_CREATE', payload: value })

  const actions = { setNewGroups, setDynamic, setOpenCreate, setUpdateGroup, setRemoveGroup, setOpenSelectParticipate, setParticipateGroupId, handleOpenSelectParticipate, setCallbackParticipate, getRanking }
  const returnValue: [typeof state, typeof actions] = [state, actions]

  return returnValue
}
